import { useState, useCallback, useEffect } from 'react';

const emptyTeamData = {
  coordenadores: [],
  areas: [],
  tecnicos: [],
};

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export function useEquipe2TeamData(apiBaseUrl) {
  const teamApiBase = `${apiBaseUrl}/api/team`;

  const [teamData, setTeamData] = useState(emptyTeamData);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErrors({});
    try {
      const headers = authHeaders();
      const [techRes, areasRes, coordRes, areaTeamRes, areaCoordRes] = await Promise.all([
        fetch(`${teamApiBase}/technicians?includeNative=1`, { headers }),
        fetch(`${teamApiBase}/areas`, { headers }),
        fetch(`${teamApiBase}/coordinators`, { headers }),
        fetch(`${teamApiBase}/areateam?includeNative=1`, { headers }),
        fetch(`${teamApiBase}/areacoord`, { headers }),
      ]);

      const [techJson, areasJson, coordJson, areaTeamJson, areaCoordJson] = await Promise.all([
        techRes.json(),
        areasRes.json(),
        coordRes.json(),
        areaTeamRes.json(),
        areaCoordRes.json(),
      ]);

      const nextErrors = {};
      if (!techJson.success) nextErrors.technicians = techJson.message;
      if (!areasJson.success) nextErrors.areas = areasJson.message;
      if (!coordJson.success) nextErrors.coordinators = coordJson.message;

      const tecnicos = techJson.success ? techJson.data : [];
      const areasRaw = areasJson.success ? areasJson.data : [];
      const coordenadores = coordJson.success ? coordJson.data : [];
      const areaTeam = areaTeamJson.success ? areaTeamJson.data : [];
      const areaCoord = areaCoordJson.success ? areaCoordJson.data : [];

      const areas = areasRaw.map((area) => {
        const vinculo = areaCoord.find((v) => v.id_area === area.id);
        return {
          ...area,
          coordenadorId: vinculo ? vinculo.id_coordinator : null,
        };
      });

      const tecnicosWithArea = tecnicos.map((tecnico) => {
        const vinculo = areaTeam.find((v) => v.id_tech === tecnico.id);
        return {
          ...tecnico,
          areaId: vinculo ? vinculo.id_area : null,
        };
      });

      setTeamData({ coordenadores, areas, tecnicos: tecnicosWithArea });
      setErrors(nextErrors);
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  }, [teamApiBase]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const createArea = useCallback(
    async (name) => {
      const response = await fetch(`${teamApiBase}/areas`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: name.trim() }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Erro ao criar área');
      const newArea = {
        id: result.data.id,
        nome: result.data.name,
        abreviacao: result.data.abreviacao || '',
        coordenadorId: null,
      };
      setTeamData((prev) => ({ ...prev, areas: [...prev.areas, newArea] }));
      return newArea;
    },
    [teamApiBase]
  );

  const updateArea = useCallback(
    async (areaId, name, abreviacao) => {
      const response = await fetch(`${teamApiBase}/areas/${areaId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ name: name.trim(), abreviacao: abreviacao.trim().slice(0, 4) }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Erro ao atualizar área');
      setTeamData((prev) => ({
        ...prev,
        areas: prev.areas.map((a) =>
          a.id === areaId ? { ...a, nome: name.trim(), abreviacao: abreviacao.trim().slice(0, 4) } : a
        ),
      }));
    },
    [teamApiBase]
  );

  const deleteArea = useCallback(
    async (areaId) => {
      const response = await fetch(`${teamApiBase}/areas/${areaId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Erro ao excluir área');
      setTeamData((prev) => ({
        ...prev,
        areas: prev.areas.filter((a) => a.id !== areaId),
      }));
    },
    [teamApiBase]
  );

  const linkTechnicianToArea = useCallback(
    async (techId, areaId) => {
      const response = await fetch(`${teamApiBase}/areateam`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ id_tech: techId, id_area: areaId }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Erro ao vincular técnico');
      setTeamData((prev) => ({
        ...prev,
        tecnicos: prev.tecnicos.map((t) => (t.id === techId ? { ...t, areaId } : t)),
      }));
    },
    [teamApiBase]
  );

  const unlinkTechnician = useCallback(
    async (techId) => {
      const response = await fetch(`${teamApiBase}/areateam/${techId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Erro ao remover técnico');
      setTeamData((prev) => ({
        ...prev,
        tecnicos: prev.tecnicos.map((t) => (t.id === techId ? { ...t, areaId: null } : t)),
      }));
    },
    [teamApiBase]
  );

  const linkAreaToCoordinator = useCallback(
    async (areaId, coordinatorId) => {
      const response = await fetch(`${teamApiBase}/areacoord`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ id_area: areaId, id_coordinator: coordinatorId }),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Erro ao vincular área');
      setTeamData((prev) => ({
        ...prev,
        areas: prev.areas.map((a) =>
          a.id === areaId ? { ...a, coordenadorId: coordinatorId } : a
        ),
      }));
    },
    [teamApiBase]
  );

  const unlinkAreaFromCoordinator = useCallback(
    async (areaId) => {
      const response = await fetch(`${teamApiBase}/areacoord/${areaId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Erro ao desvincular área');
      setTeamData((prev) => ({
        ...prev,
        areas: prev.areas.map((a) => (a.id === areaId ? { ...a, coordenadorId: null } : a)),
      }));
    },
    [teamApiBase]
  );

  return {
    teamData,
    loading,
    errors,
    refresh: loadAll,
    createArea,
    updateArea,
    deleteArea,
    linkTechnicianToArea,
    unlinkTechnician,
    linkAreaToCoordinator,
    unlinkAreaFromCoordinator,
  };
}
